import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import {
  Table,
  Input,
  Select,
  Button,
  Tabs,
  Modal,
  message,
  Tooltip,
  Avatar,
  Spin,
  Row,
  Col,
  Card,
  ConfigProvider,
  Space,
  Typography,
} from "antd";
import {
  SearchOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";

import useDebounce from "./usedebounce";
import services from "../../services";
import ROUTES_PATH from "../../constants/routes";
import { messages } from "../../constants/message/messages";
import defaultuser from "./assets/defaultuser.png";
import DashboardLayout from "../../layout/DashboardLayout/DashboardLayout";
import "./users.css";

const { Title, Text } = Typography;

const Users = () => {
  const [value, setValue] = useState(0);
  const [searchedVal, setSearchedVal] = useState("");
  const [blockedSearchedVal, setBlockedSearchedVal] = useState("");
  const [activeUsers, setActiveUsers] = useState([]);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [results, setResults] = useState([]);
  const [blockResults, setBlockResults] = useState([]);
  const [loader, setLoader] = useState(false);
  const [blockUserDialog, setBlockUserDialog] = useState({
    id: "",
    status: false,
  });
  const [unblockUserDialog, setUnblockUserDialog] = useState({
    id: "",
    status: false,
  });
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [blockedPage, setBlockedPage] = useState(0);
  const [blockedRowsPerPage, setBlockedRowsPerPage] = useState(10);

  const [sortDirection, setSortDirection] = useState("asc");
  const [isLoading, setIsLoading] = useState(true);
  const [totalActiveUsers, setTotalActiveUsers] = useState(0);
  const [totalBlockedUsers, setTotalBlockedUsers] = useState(0);

  const [values, setValues] = useState({
    reasonText: "",
    error: true,
    isReasonError: false,
    reasonErrorMsg: "",
  });

  const debouncedSearchTerm = useDebounce(searchedVal, 500);
  const debouncedBlockSearch = useDebounce(blockedSearchedVal, 500);

  const navigate = useNavigate();

  const handleReasonChangeValue = (prop, val) => {
    setValues((prev) => ({ ...prev, [prop]: val }));
  };

  const handleRowClick = (record) => {
    setLoader(true);
    navigate(ROUTES_PATH.DASHBOARD + "/users/profile", {
      state: { user: record.id },
    });
    setLoader(false);
  };

  const changeloadingStatus = (statusState) => {
    setLoadingStatus(statusState);
  };

  // Handle blocking of users
  const handleBlock = (id) => {
    if (validate()) {
      changeloadingStatus(true);
      const filteredUsers = activeUsers.reduce(
        (acc, obj) => {
          if (obj.user.id === id) {
            acc.filtered.push(obj);
          } else {
            acc.unfiltered.push(obj);
          }
          return acc;
        },
        { filtered: [], unfiltered: [] }
      );

      let blockedUser = {
        id: `${id}`,
        status: true,
        reason: values.reasonText,
      };

      services.changeUserActiveStatus(blockedUser).then((res) => {
        if (res.status === 200 || res.status === 201) {
          setActiveUsers(filteredUsers.unfiltered);

          const _newBlockedUser = filteredUsers.filtered[0];
          const newBlockedUser = {
            ..._newBlockedUser,
            user: { ..._newBlockedUser?.user, blocked: true },
          };
          setBlockedUsers((prevObjects) => {
            const obj = [...prevObjects];
            obj.unshift(newBlockedUser);
            return obj;
          });

          setBlockUserDialog({ id: "", status: false });
          changeloadingStatus(false);
          setTotalActiveUsers(totalActiveUsers - 1);
          setTotalBlockedUsers(totalBlockedUsers + 1);
          setValues((prev) => ({
            ...prev,
            reasonText: "",
            isReasonError: false,
            reasonErrorMsg: "",
          }));
        } else {
          message.error(messages.somethingWentWrong);
          changeloadingStatus(false);
          setBlockUserDialog({
            id: "",
            status: false,
          });
        }
      });
    }
  };

  // Handling unblocking of users
  const handleUnblock = (id) => {
    changeloadingStatus(true);
    const filteredUsers = blockedUsers.reduce(
      (acc, obj) => {
        if (obj?.user.id === id) {
          acc.filtered.push(obj);
        } else {
          acc.unfiltered.push(obj);
        }
        return acc;
      },
      { filtered: [], unfiltered: [] }
    );

    let unBlockedUser = {
      id: `${id}`,
      status: false,
    };

    services.changeUserActiveStatus(unBlockedUser).then((res) => {
      if (res.status === 200 || res.status === 201) {
        setBlockedUsers(filteredUsers.unfiltered);

        const _newActiveUser = filteredUsers.filtered[0];
        const newActiveUser = {
          ..._newActiveUser,
          user: { ..._newActiveUser?.user, blocked: false },
        };
        setActiveUsers((prevObjects) => {
          const obj = [...prevObjects];
          obj.unshift(newActiveUser);
          return obj;
        });
        setUnblockUserDialog({ id: "", status: false });
        changeloadingStatus(false);
        setTotalActiveUsers(totalActiveUsers + 1);
        setTotalBlockedUsers(totalBlockedUsers - 1);
      } else {
        message.error(messages.somethingWentWrong);
        changeloadingStatus(false);
        setUnblockUserDialog({
          id: "",
          status: false,
        });
      }
    });
  };

  const validate = () => {
    let { reasonText } = values;
    if (reasonText.trim() === "") {
      handleReasonChangeValue("isReasonError", true);
      handleReasonChangeValue("reasonErrorMsg", messages.reasonRequired);
      return false;
    } else if (reasonText.trim().length < 3) {
      handleReasonChangeValue("isReasonError", true);
      handleReasonChangeValue("reasonErrorMsg", messages.validReasonRequired);
      return false;
    } else if (reasonText.trim().length > 300) {
      handleReasonChangeValue("isReasonError", true);
      handleReasonChangeValue("reasonErrorMsg", messages.validReasonRequired);
      return false;
    } else {
      return true;
    }
  };

  useEffect(() => {
    const activeUser = [];

    if (value === 0) {
      setIsLoading(true);
      services
        .getUsersList(rowsPerPage, page === 0 ? 1 : page + 1, sortDirection)
        .then((res) => {
          res?.data?.data?.activeUsers.forEach((item) => {
            activeUser.push(item);
          });

          setTotalActiveUsers(res?.data?.data?.activeUsersCount || 0);
          setTotalBlockedUsers(res?.data?.data?.blockedUsersCount || 0);

          setActiveUsers(activeUser);
          setIsLoading(false);
        })
        .catch((error) => {
          console.log(error);
          setIsLoading(false);
        });
    }
  }, [page, sortDirection, totalActiveUsers, totalBlockedUsers, value]);

  useEffect(() => {
    const blockUser = [];

    if (value === 1) {
      setIsLoading(true);
      services
        .getUsersList(
          blockedRowsPerPage,
          blockedPage === 0 ? 1 : blockedPage + 1,
          sortDirection
        )
        .then((res) => {
          res?.data?.data?.blockedUsers.forEach((item) => {
            blockUser.push(item);
          });

          setBlockedUsers(blockUser);
          setIsLoading(false);
        })
        .catch((error) => {
          console.log(error);
          setIsLoading(false);
        });
    }
  }, [blockedPage, sortDirection, value]);

  useEffect(() => {
    const trimmedTerm = (debouncedSearchTerm || "").trim().replace(/\s+/g, " ");
    if (trimmedTerm) {
      setIsLoading(true);
      const terms = trimmedTerm.toLowerCase().split(" ");
      const searchParam = terms[0];
      let params = {
        userName: searchParam,
        active: true,
      };

      services
        .searchUser(params)
        .then((res) => {
          const userList = res?.data?.data?.userList || [];
          const filteredActiveUsers = userList.filter((item) => {
            const userObj = item?.user || item;
            const firstName = (userObj?.firstName || "").toLowerCase();
            const lastName = (userObj?.lastName || "").toLowerCase();
            const email = (userObj?.email || "").toLowerCase();
            const fullName = `${firstName} ${lastName}`;
            return terms.every(
              (term) =>
                firstName.includes(term) ||
                lastName.includes(term) ||
                fullName.includes(term) ||
                email.includes(term)
            );
          });

          setResults(filteredActiveUsers);
          setIsLoading(false);
        })
        .catch((e) => {
          console.log(e);
          setIsLoading(false);
        });
    } else {
      setResults([]);
      setActiveUsers(activeUsers);
    }
  }, [debouncedSearchTerm, activeUsers]);

  useEffect(() => {
    const trimmedTerm = (debouncedBlockSearch || "").trim().replace(/\s+/g, " ");
    if (trimmedTerm) {
      setIsLoading(true);
      const terms = trimmedTerm.toLowerCase().split(" ");
      const searchParam = terms[0];
      let params = {
        userName: searchParam,
        active: false,
      };

      services
        .searchUser(params)
        .then((res) => {
          const userList = res?.data?.data?.userList || [];
          const filteredBlockedUsers = userList.filter((item) => {
            const userObj = item?.user || item;
            const firstName = (userObj?.firstName || "").toLowerCase();
            const lastName = (userObj?.lastName || "").toLowerCase();
            const email = (userObj?.email || "").toLowerCase();
            const fullName = `${firstName} ${lastName}`;
            return terms.every(
              (term) =>
                firstName.includes(term) ||
                lastName.includes(term) ||
                fullName.includes(term) ||
                email.includes(term)
            );
          });

          setBlockResults(filteredBlockedUsers);
          setIsLoading(false);
        })
        .catch((e) => {
          console.log(e);
          setIsLoading(false);
        });
    } else {
      setBlockResults([]);
      setBlockedUsers(blockedUsers);
    }
  }, [debouncedBlockSearch, blockedUsers]);

  // Unified Data Mapping Functions
  const mapActiveUser = (item) => {
    const userObj = item?.user || item;
    return {
      key: userObj?.id || item?.id,
      id: userObj?.id || item?.id,
      fullName: `${userObj?.firstName || ""} ${userObj?.lastName || ""}`.trim() || "Unknown User",
      profileImage: userObj?.profileImage,
      rank: item?.userRank ?? "--",
      email: userObj?.email || "N/A",
      carbonEmission: item?.carbonEmission ?? 0,
      carbonOffset: item?.carbonOffset ?? 0,
    };
  };

  const mapBlockedUser = (item) => {
    const userObj = item?.user || item;
    return {
      key: userObj?.id || item?.id,
      id: userObj?.id || item?.id,
      fullName: `${userObj?.firstName || ""} ${userObj?.lastName || ""}`.trim() || "Unknown User",
      profileImage: userObj?.profileImage,
      rank: "--",
      email: userObj?.email || "N/A",
      carbonEmission: item?.carbonEmission ?? 0,
      carbonOffset: item?.carbonOffset ?? 0,
      deletedReason: userObj?.deletedReason || item?.deletedReason || "N/A",
    };
  };

  // Ant Design Table Columns Config
  const activeColumns = [
    {
      title: "Full Name",
      dataIndex: "fullName",
      key: "fullName",
      width: 250,
      render: (text, record) => (
        <Space>
          <Avatar
            src={record.profileImage || defaultuser}
            alt="User Icon"
            size={36}
          />
          <Text strong>{text}</Text>
        </Space>
      ),
    },
    {
      title: "Rank",
      dataIndex: "rank",
      key: "rank",
      width: 100,
    },
    {
      title: "Email Id",
      dataIndex: "email",
      key: "email",
      width: 220,
      render: (text) => (
        <Tooltip title={text} arrow>
          <span style={{
            display: "block",
            maxWidth: "180px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis"
          }}>
            {text}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Co2 Emissions",
      dataIndex: "carbonEmission",
      key: "carbonEmission",
      width: 150,
      render: (val) => `${val || 0} Kg`,
    },
    {
      title: "Co2 Saved",
      dataIndex: "carbonOffset",
      key: "carbonOffset",
      width: 150,
      render: (val) => `${val || 0} Kg`,
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Button
          type="text"
          danger
          onClick={(e) => {
            e.stopPropagation();
            setBlockUserDialog({
              id: record.id,
              status: true,
            });
          }}
        >
          Block
        </Button>
      ),
    },
  ];

  const blockedColumns = [
    {
      title: "Full Name",
      dataIndex: "fullName",
      key: "fullName",
      width: 250,
      render: (text, record) => (
        <Space>
          <Avatar
            src={record.profileImage || defaultuser}
            alt="User Icon"
            size={36}
          />
          <Text strong>{text}</Text>
        </Space>
      ),
    },
    {
      title: "Rank",
      dataIndex: "rank",
      key: "rank",
      width: 100,
    },
    {
      title: "Email Id",
      dataIndex: "email",
      key: "email",
      width: 220,
      render: (text) => (
        <Tooltip title={text} arrow>
          <span style={{
            display: "block",
            maxWidth: "180px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis"
          }}>
            {text}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Co2 Emissions",
      dataIndex: "carbonEmission",
      key: "carbonEmission",
      width: 150,
      render: (val) => `${val || 0} Kg`,
    },
    {
      title: "Co2 Saved",
      dataIndex: "carbonOffset",
      key: "carbonOffset",
      width: 150,
      render: (val) => `${val || 0} Kg`,
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Button
          type="text"
          style={{ color: "#1890ff" }}
          onClick={(e) => {
            e.stopPropagation();
            setUnblockUserDialog({
              id: record.id,
              status: true,
            });
          }}
        >
          Unblock
        </Button>
      ),
    },
    {
      title: "",
      key: "info",
      width: 50,
      render: (_, record) => (
        <Tooltip title={record.deletedReason} arrow>
          <InfoCircleOutlined
            style={{
              position: "relative",
              top: "2px",
              fontSize: "16px",
              color: "#667085",
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#5FB92A",
          fontFamily: "'Manrope', sans-serif",
          borderRadius: 8,
        },
      }}
    >
      <DashboardLayout>
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Card bordered={false} style={{ boxShadow: "none" }}>
            {/* Header Section */}
            <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
              <Col>
                <Title level={3} style={{ margin: 0, fontWeight: 600 }}>
                  Users
                </Title>
                <Text type="secondary">
                  Manage your active and blocked users.
                </Text>
              </Col>
            </Row>

            {/* Tabs */}
            <Tabs
              activeKey={String(value)}
              onChange={(key) => setValue(Number(key))}
              items={[
                {
                  key: "0",
                  label: `Active Users (${totalActiveUsers})`,
                },
                {
                  key: "1",
                  label: `Blocked Users (${totalBlockedUsers})`,
                },
              ]}
              style={{ marginBottom: 24 }}
            />

            {/* Filters Section (Search & Sort) */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }} align="middle" justify="space-between">
              <Col xs={24} sm={12} md={8}>
                <Input
                  placeholder="Search for Users"
                  value={value === 0 ? searchedVal : blockedSearchedVal}
                  onChange={(e) => {
                    if (value === 0) {
                      setSearchedVal(e.target.value);
                    } else {
                      setBlockedSearchedVal(e.target.value);
                    }
                  }}
                  prefix={<SearchOutlined style={{ color: "#667085" }} />}
                  allowClear
                  style={{ height: "40px", borderRadius: "8px" }}
                />
              </Col>
              
              {value === 0 && (
                <Col>
                  <Space align="center">
                    <span style={{ fontSize: "14px", color: "#667085" }}>Sort by Rank:</span>
                    <Select
                      value={sortDirection}
                      onChange={(val) => setSortDirection(val)}
                      style={{ width: 160, height: 40 }}
                      options={[
                        { value: "asc", label: "Low to High" },
                        { value: "desc", label: "High to Low" },
                      ]}
                    />
                  </Space>
                </Col>
              )}
            </Row>

            {/* Table Section */}
            <div className="users-table-wrapper">
              {isLoading && (
                <div className="table-progress-bar">
                  <div className="table-progress-bar-inner" />
                </div>
              )}
              {value === 0 ? (
                <Table
                  columns={activeColumns}
                  dataSource={
                    debouncedSearchTerm?.trim()
                      ? results.map(mapActiveUser)
                      : activeUsers.map(mapActiveUser)
                  }
                  loading={false}
                  onRow={(record) => ({
                    onClick: () => handleRowClick(record),
                  })}
                  pagination={
                    debouncedSearchTerm?.trim()
                      ? false
                      : {
                          position: ["bottomCenter"],
                          showSizeChanger: false,
                          hideOnSinglePage: true,
                          current: page + 1,
                          pageSize: rowsPerPage,
                          total: totalActiveUsers,
                          showPrevNextJumpers: false,
                          onChange: (p) => setPage(p - 1),
                          itemRender: (current, type, originalElement) => {
                            const totalPages = Math.ceil(totalActiveUsers / rowsPerPage);
                            if (type === "prev") {
                              if (totalPages <= 1) return null;
                              return (
                                <Button className="users-pagination-link" disabled={page <= 0}>
                                  ← Previous
                                </Button>
                              );
                            }
                            if (type === "next") {
                              if (totalPages <= 1) return null;
                              return (
                                <Button className="users-pagination-link" disabled={page >= totalPages - 1}>
                                  Next →
                                </Button>
                              );
                            }
                            return originalElement;
                          },
                        }
                  }
                />
              ) : (
                <Table
                  columns={blockedColumns}
                  dataSource={
                    debouncedBlockSearch?.trim()
                      ? blockResults.map(mapBlockedUser)
                      : blockedUsers.map(mapBlockedUser)
                  }
                  loading={false}
                  onRow={(record) => ({
                    onClick: () => handleRowClick(record),
                  })}
                  pagination={
                    debouncedBlockSearch?.trim()
                      ? false
                      : {
                          position: ["bottomCenter"],
                          showSizeChanger: false,
                          hideOnSinglePage: true,
                          current: blockedPage + 1,
                          pageSize: blockedRowsPerPage,
                          total: totalBlockedUsers,
                          showPrevNextJumpers: false,
                          onChange: (p) => setBlockedPage(p - 1),
                          itemRender: (current, type, originalElement) => {
                            const totalPages = Math.ceil(totalBlockedUsers / blockedRowsPerPage);
                            if (type === "prev") {
                              if (totalPages <= 1) return null;
                              return (
                                <Button className="users-pagination-link" disabled={blockedPage <= 0}>
                                  ← Previous
                                </Button>
                              );
                            }
                            if (type === "next") {
                              if (totalPages <= 1) return null;
                              return (
                                <Button className="users-pagination-link" disabled={blockedPage >= totalPages - 1}>
                                  Next →
                                </Button>
                              );
                            }
                            return originalElement;
                          },
                        }
                  }
                />
              )}
            </div>
          </Card>
        </Box>

        {/* Block User Modal */}
        <Modal
          open={blockUserDialog.status}
          title="Block User"
          okText="Yes"
          cancelText="No"
          confirmLoading={loadingStatus}
          onCancel={() => {
            setBlockUserDialog({ id: "", status: false });
            setValues({
              reasonText: "",
              error: true,
              isReasonError: false,
              reasonErrorMsg: "",
            });
          }}
          onOk={() => {
            handleBlock(blockUserDialog.id);
          }}
          okButtonProps={{
            style: { backgroundColor: "#5FB92A", borderColor: "#5FB92A" },
          }}
        >
          <div style={{ padding: "12px 0" }}>
            <p style={{ marginBottom: "16px" }}>Are you sure you want to block the user?</p>
            <Input.TextArea
              placeholder="Reason"
              rows={3}
              maxLength={300}
              showCount
              value={values.reasonText}
              onChange={(e) => {
                setValues((prev) => ({
                  ...prev,
                  reasonText: e.target.value,
                  isReasonError: false,
                  reasonErrorMsg: "",
                }));
              }}
              status={values.isReasonError ? "error" : ""}
            />
            {values.isReasonError && (
              <div style={{ color: "#ff4d4f", marginTop: "4px", fontSize: "14px" }}>
                {values.reasonErrorMsg}
              </div>
            )}
          </div>
        </Modal>

        {/* Unblock User Modal */}
        <Modal
          open={unblockUserDialog.status}
          title="Unblock User"
          okText="Yes"
          cancelText="No"
          confirmLoading={loadingStatus}
          onCancel={() => setUnblockUserDialog({ id: "", status: false })}
          onOk={() => handleUnblock(unblockUserDialog.id)}
          okButtonProps={{
            style: { backgroundColor: "#5FB92A", borderColor: "#5FB92A" },
          }}
        >
          <div style={{ padding: "12px 0" }}>
            <p>Are you sure you want to unblock the user?</p>
          </div>
        </Modal>
      </DashboardLayout>
    </ConfigProvider>
  );
};

export default Users;
